/**
 * Web Audio API Audio Synthesizer & Speech Announcer Engine
 * Creates rich sound effects for score points, game wins, set wins, match wins, undo, and settings interaction.
 * Includes Text-to-Speech (TTS) score calling.
 */

class AudioSynthesizer {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.volume = 0.8;
    this.isInitialized = false;
    this.audioCache = new Map();
    this.currentAudio = null;
  }

  init() {
    if (typeof window === 'undefined') return;

    if (this.isInitialized && this.ctx) {
      if (this.ctx.state === 'suspended') {
        this.ctx.resume().catch(() => {});
      }
    } else {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          this.ctx = new AudioCtx();
          this.masterGain = this.ctx.createGain();
          this.masterGain.gain.setValueAtTime(this.volume, this.ctx.currentTime);
          this.masterGain.connect(this.ctx.destination);
          this.isInitialized = true;
        }
      } catch (e) {
        console.warn('Web Audio API not supported:', e);
      }
    }

    if (window.speechSynthesis && window.speechSynthesis.paused) {
      try {
        window.speechSynthesis.resume();
      } catch (_) {}
    }
  }

  setSpeechEnabled(enabled) {
    this.isSpeechEnabled = Boolean(enabled);
    if (!this.isSpeechEnabled) {
      if (this.currentAudio) {
        try { this.currentAudio.pause(); } catch (_) {}
        this.currentAudio = null;
      }
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        try { window.speechSynthesis.cancel(); } catch (_) {}
      }
    }
  }

  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setValueAtTime(this.volume, this.ctx.currentTime);
    }
  }

  // Play a simple tone
  playTone(freq, type = 'sine', duration = 0.15, gainValue = 0.3) {
    if (!this.isInitialized || !this.ctx) return;
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime);

      gain.gain.setValueAtTime(gainValue, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start();
      osc.stop(this.ctx.currentTime + duration);
    } catch (e) {
      console.warn('Audio tone error:', e);
    }
  }

  // Point Score Sound Effect (Upward Arpeggio / Chime)
  playPoint(team = 'A') {
    this.init();
    if (!this.ctx) return;

    const baseFreq = team === 'A' ? 523.25 : 659.25; // C5 or E5
    const now = this.ctx.currentTime;

    [0, 0.08].forEach((delay, idx) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(baseFreq * (idx === 0 ? 1 : 1.25), now + delay);

      gain.gain.setValueAtTime(0.25, now + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.2);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(now + delay);
      osc.stop(now + delay + 0.2);
    });
  }

  // Game Win Fanfare
  playGame() {
    this.init();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6

    notes.forEach((freq, idx) => {
      const delay = idx * 0.09;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + delay);

      gain.gain.setValueAtTime(0.35, now + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.3);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(now + delay);
      osc.stop(now + delay + 0.3);
    });
  }

  // Set Win Celebration Sound
  playSet() {
    this.init();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    const notes = [440, 554.37, 659.25, 880]; // A4, C#5, E5, A5

    notes.forEach((freq, idx) => {
      const delay = idx * 0.12;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + delay);

      gain.gain.setValueAtTime(0.4, now + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.4);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(now + delay);
      osc.stop(now + delay + 0.4);
    });
  }

  // Match Win Grand Fanfare
  playMatch() {
    this.init();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    const chords = [
      [523.25, 659.25, 783.99], // C Major
      [587.33, 698.46, 880.00], // D Minor
      [659.25, 783.99, 1046.50], // E Minor / C Major
    ];

    chords.forEach((chord, step) => {
      const stepDelay = step * 0.25;
      chord.forEach((freq) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(freq, now + stepDelay);

        gain.gain.setValueAtTime(0.15, now + stepDelay);
        gain.gain.exponentialRampToValueAtTime(0.001, now + stepDelay + 0.5);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now + stepDelay);
        osc.stop(now + stepDelay + 0.5);
      });
    });
  }

  // Undo Sound
  playUndo() {
    this.init();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, now);
    osc.frequency.exponentialRampToValueAtTime(200, now + 0.15);

    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.15);
  }

  // Helper to map score phrase or numbers to pre-rendered audio file
  matchClipKey(phrase) {
    if (!phrase) return null;
    const clean = String(phrase).toLowerCase().trim()
      .replace(/[.,\-\u2013]/g, ' ')
      .replace(/\s+/g, ' ');

    const map = {
      'love all': 'love-all',
      '0 0': 'love-all',
      'zero all': 'love-all',
      'fifteen love': '15-love',
      '15 0': '15-love',
      '15 love': '15-love',
      'love fifteen': 'love-15',
      '0 15': 'love-15',
      'love 15': 'love-15',
      'fifteen all': '15-all',
      '15 15': '15-all',
      '15 all': '15-all',
      'thirty love': '30-love',
      '30 0': '30-love',
      '30 love': '30-love',
      'love thirty': 'love-30',
      '0 30': 'love-30',
      'love 30': 'love-30',
      'thirty fifteen': '30-15',
      '30 15': '30-15',
      'fifteen thirty': '15-30',
      '15 30': '15-30',
      'thirty all': '30-all',
      '30 30': '30-all',
      '30 all': '30-all',
      'forty love': '40-love',
      '40 0': '40-love',
      '40 love': '40-love',
      'love forty': 'love-40',
      '0 40': 'love-40',
      'love 40': 'love-40',
      'forty fifteen': '40-15',
      '40 15': '40-15',
      'fifteen forty': '15-40',
      '15 40': '15-40',
      'forty thirty': '40-30',
      '40 30': '40-30',
      'thirty forty': '30-40',
      '30 40': '30-40',
      'deuce': 'deuce',
      '40 40': 'deuce',
      'forty all': 'deuce',
      'advantage': 'advantage',
      'advantage server': 'advantage-server',
      'advantage receiver': 'advantage-receiver',
      'golden point': 'golden-point',
      'game and set': 'game-set',
      'game set': 'game-set',
      'game set and match': 'game-set-match',
      'game set match': 'game-set-match',
      'game': 'game',
      'game won': 'game',
      'tiebreak': 'tiebreak',
      'testing': 'testing',
      'testing audio': 'testing',
      'testing fifteen love': 'testing'
    };

    if (map[clean]) return map[clean];

    // Check exact & partial patterns
    if (clean.startsWith('testing')) return 'testing';
    if (clean === 'game and set' || clean === 'game and the set') return 'game-set';
    if (clean === 'game set and match' || clean === 'game set match') return 'game-set-match';
    if (clean === 'game' || clean === 'game won') return 'game';
    if (clean.includes('golden point') || clean.includes('deciding point')) return 'golden-point';
    if (clean === 'advantage' || clean.startsWith('advantage ')) return 'advantage';
    if (clean === 'deuce') return 'deuce';
    if (clean === 'tiebreak') return 'tiebreak';

    // Direct numbers (e.g. Rotation Mode "3 1" or "4 2")
    const numMatch = clean.match(/^(\d+)\s+(\d+)$/);
    if (numMatch) {
      const n1 = parseInt(numMatch[1], 10);
      const n2 = parseInt(numMatch[2], 10);
      if (n1 === n2) {
        if (n1 === 0) return 'love-all';
        if (n1 === 1) return 'num-1';
        return `num-${n1}`;
      }
    }

    return null;
  }

  // Resolve phrase or game win announcement into a sequence of audio clips
  resolvePhraseToClips(phrase) {
    if (!phrase) return null;
    const raw = String(phrase).trim();
    const clean = raw.toLowerCase()
      .replace(/[.,\-\u2013]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // 1. Direct single-clip check
    const single = this.matchClipKey(clean);
    if (single) return [single];

    // 2. Parse game announcement phrases
    // Example: "Game Johnny and Caroline. Johnny and Caroline leading 3 games to 1."
    // Example: "Game David and Marty. David and Marty leading 2 games to 1."
    // Example: "Game David and Marty. 2 games all."
    // Example: "Game David. David leading 1 game to love."
    if (clean.startsWith('game')) {
      const clips = ['game'];
      
      const nameKeys = [
        'johnny', 'caroline', 'david', 'marty', 'firedoor', 'alex', 'chris', 'sam', 'ben',
        'dan', 'tom', 'mike', 'nick', 'paul', 'rob', 'mark', 'matt', 'james', 'will',
        'luke', 'jack', 'joe', 'max', 'steve', 'george', 'harry', 'leo', 'oliver',
        'charlie', 'liam', 'noah', 'sarah', 'emma', 'laura', 'emily', 'lucy', 'sophie',
        'chloe', 'olivia', 'mia', 'anna', 'hannah', 'zoe', 'ella'
      ];

      const words = clean.split(' ');
      const namesFound = [];
      for (const w of words) {
        if (nameKeys.includes(w) && !namesFound.includes(w)) {
          namesFound.push(w);
        }
      }

      // Check if we have a direct natural pre-rendered pair clip (e.g. "pair-david-marty")
      if (namesFound.length >= 2) {
        const pairKey = `pair-${namesFound[0]}-${namesFound[1]}`;
        clips.push(pairKey);
      } else if (namesFound.length === 1) {
        clips.push(`name-${namesFound[0]}`);
      }

      // Check tie announcement (e.g. "2 games all" -> "all-2")
      const allMatch = clean.match(/(\d+)\s+games?\s+all/);
      if (allMatch) {
        const n = parseInt(allMatch[1], 10);
        if (n >= 1 && n <= 6) {
          clips.push(`all-${n}`);
        } else {
          clips.push(`num-${n}`);
          clips.push('word-games-all');
        }
        return clips;
      }

      // Check lead announcement (e.g. "leading 2 games to 1" -> "lead-2-1")
      const leadMatch = clean.match(/(\d+)\s+games?\s+to\s+(\d+|love|zero)/i) || clean.match(/lead\w*\s+(\d+)\s+(?:games?\s+)?to\s+(\d+|love|zero)/i);
      if (leadMatch) {
        const n1 = parseInt(leadMatch[1], 10);
        const n2Raw = leadMatch[2].toLowerCase();
        const n2 = (n2Raw === 'love' || n2Raw === 'zero' || n2Raw === '0') ? 0 : parseInt(n2Raw, 10);
        
        const compoundKey = `lead-${n1}-${n2}`;
        clips.push(compoundKey);
        return clips;
      }

      return clips.length > 0 ? clips : ['game'];
    }

    return null;
  }

  // Load and cache decoded Web Audio buffers for instant 0ms latency playback
  async loadAudioBuffer(key) {
    if (!this.ctx) this.init();
    if (!this.ctx) return null;
    if (!this._bufferCache) this._bufferCache = new Map();
    if (this._bufferCache.has(key)) return this._bufferCache.get(key);

    try {
      const url = `${process.env.PUBLIC_URL || ''}/audio/${key}.m4a`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const arrayBuffer = await resp.arrayBuffer();
      const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
      this._bufferCache.set(key, audioBuffer);
      return audioBuffer;
    } catch (e) {
      try {
        const wavUrl = `${process.env.PUBLIC_URL || ''}/audio/${key}.wav`;
        const resp = await fetch(wavUrl);
        const arrayBuffer = await resp.arrayBuffer();
        const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
        this._bufferCache.set(key, audioBuffer);
        return audioBuffer;
      } catch (err) {
        console.warn(`[PadelMVP Audio] loadAudioBuffer error for ${key}:`, err);
        return null;
      }
    }
  }

  // Play single pre-rendered human audio clip via HTML5 Audio
  playAudioClip(clipKey) {
    if (typeof window === 'undefined') return false;
    try {
      if (this.currentAudio) {
        try { this.currentAudio.pause(); } catch (_) {}
        this.currentAudio = null;
      }

      const audioPath = `${process.env.PUBLIC_URL || ''}/audio/${clipKey}.m4a`;
      const audio = new Audio(audioPath);
      audio.volume = this.volume || 0.8;
      this.currentAudio = audio;

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          console.log(`[PadelMVP Audio] 🔊 Played Audio Clip: "${clipKey}"`);
        }).catch((err) => {
          console.warn(`[PadelMVP Audio] Audio play error for ${clipKey}, trying .wav:`, err);
          const wavAudio = new Audio(`${process.env.PUBLIC_URL || ''}/audio/${clipKey}.wav`);
          wavAudio.volume = this.volume || 0.8;
          wavAudio.play().catch(() => {});
        });
      }
      return true;
    } catch (e) {
      console.warn('[PadelMVP Audio] playAudioClip error:', e);
      return false;
    }
  }

  // Play a sequence of audio clips with zero gaps and natural conversational flow
  async playAudioSequence(clipKeys) {
    if (!Array.isArray(clipKeys) || clipKeys.length === 0 || typeof window === 'undefined') return false;

    this.init();
    if (this.ctx && this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch (_) {}
    }

    console.log(`[PadelMVP Audio] 🔊 Playing Natural Sequence: ${clipKeys.join(' -> ')}`);

    try {
      // 1. Primary: Web Audio API scheduling (seamless broadcast-grade timing, 0ms latency)
      if (this.ctx) {
        const buffers = await Promise.all(clipKeys.map((k) => this.loadAudioBuffer(k)));
        const validBuffers = buffers.filter(Boolean);
        if (validBuffers.length === clipKeys.length) {
          let startTime = this.ctx.currentTime + 0.03;
          validBuffers.forEach((buf, idx) => {
            const source = this.ctx.createBufferSource();
            source.buffer = buf;
            source.connect(this.masterGain);
            source.start(startTime);
            // 70ms pause after "Game", 30ms between pair names and score
            const gap = idx === 0 ? 0.07 : 0.03;
            startTime += buf.duration + gap;
          });
          return true;
        }
      }
    } catch (err) {
      console.warn('[PadelMVP Audio] WebAudio sequence failed, falling back to Audio elements:', err);
    }

    // 2. Fallback: HTML5 Audio chaining
    let index = 0;
    const playNext = () => {
      if (index >= clipKeys.length) {
        this.currentAudio = null;
        return;
      }
      const key = clipKeys[index++];
      if (!key) {
        playNext();
        return;
      }

      const audio = new Audio(`${process.env.PUBLIC_URL || ''}/audio/${key}.m4a`);
      audio.volume = this.volume || 0.8;
      this.currentAudio = audio;

      audio.onended = () => {
        playNext();
      };
      audio.onerror = () => {
        const wav = new Audio(`${process.env.PUBLIC_URL || ''}/audio/${key}.wav`);
        wav.volume = this.volume || 0.8;
        this.currentAudio = wav;
        wav.onended = () => playNext();
        wav.onerror = () => playNext();
        wav.play().catch(() => playNext());
      };

      audio.play().catch((err) => {
        console.warn(`[PadelMVP Audio] Error playing sequence clip ${key}, skipping:`, err);
        playNext();
      });
    };

    playNext();
    return true;
  }

  // Bulletproof Score Announcer: Uses Audio Bank clips/sequences first, falls back to WebSpeech
  speakScore(phrase) {
    console.log(`[PadelMVP Audio] 🗣️ speakScore: "${phrase}" | enabled: ${this.isSpeechEnabled}`);
    if (!this.isSpeechEnabled || !phrase || typeof window === 'undefined') return;

    this.init();

    const text = String(phrase).trim();
    if (!text) return;

    if (this._speakTimer) {
      clearTimeout(this._speakTimer);
    }

    // 200ms delay allows the point chime to sound before the voice announcement begins
    this._speakTimer = setTimeout(() => {
      // 1. Primary: Resolve phrase to pre-rendered audio sound bank clip(s)
      const clipKeys = this.resolvePhraseToClips(text);
      if (clipKeys && clipKeys.length > 0) {
        if (clipKeys.length === 1) {
          const played = this.playAudioClip(clipKeys[0]);
          if (played) return;
        } else {
          const played = this.playAudioSequence(clipKeys);
          if (played) return;
        }
      }

      // 2. Fallback: Web Speech API for any unrecognized phrases
      if (window.speechSynthesis) {
        try {
          if (window.speechSynthesis.paused) {
            try { window.speechSynthesis.resume(); } catch (_) {}
          }
          if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
            try { window.speechSynthesis.cancel(); } catch (_) {}
          }
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = 'en-US';
          utterance.rate = 0.92;
          utterance.volume = 1.0;
          this.currentUtterance = utterance;
          window.speechSynthesis.speak(utterance);
        } catch (_) {}
      }
    }, 200);
  }
}

export const synth = new AudioSynthesizer();


